import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { useLocation, useNavigate } from "react-router-dom";
import { scrollToId, EASE } from "../../lib/scroll";
import { Logo } from "./Logo";

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="relative flex rounded-full border border-white/15 p-1 font-mono text-[11px]" data-testid="lang-switch">
      {["it", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}-btn`}
          onClick={() => setLang(l)}
          className={`relative z-10 rounded-full px-3 py-1 uppercase tracking-widest transition-colors duration-300 ${lang === l ? "text-[#0a0a0b]" : "text-white/60 hover:text-white"}`}
        >
          {lang === l && <motion.span layoutId="lang-pill" className="absolute inset-0 -z-10 rounded-full bg-[#d4af37]" transition={{ duration: 0.5, ease: EASE }} />}
          {l}
        </button>
      ))}
    </div>
  );
};

export const Header = ({ ready, onContact }) => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (id === "caso-0") { navigate("/case-0"); return; }
    if (pathname !== "/") { navigate(`/#${id}`); return; }
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={ready ? { y: 0 } : {}}
      transition={{ duration: 1, ease: EASE, delay: pathname === "/" ? 0.6 : 0 }}
      className={`fixed inset-x-0 top-0 z-50 text-[#fafaf7] transition-colors duration-500 ${scrolled || pathname !== "/" ? "border-b border-white/10 bg-[#0a0a0b]/90 backdrop-blur-xl" : "bg-transparent"}`}
    >
      <div className="wrap flex h-[72px] items-center justify-between">
        <button data-testid="header-logo" onClick={() => go("top")} aria-label="ByteVision"><Logo /></button>
        <nav className="hidden items-center gap-8 lg:flex">
          {t.nav.map((n) => (
            <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => go(n.id)} className="group relative text-[13px] text-white/70 transition-colors hover:text-white">
              {n.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#d4af37] transition-[width] duration-500 group-hover:w-full" />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <LangSwitch />
          <button data-testid="header-cta-button" onClick={onContact} className="hidden items-center gap-2 rounded-full bg-[#fafaf7] px-5 py-2.5 text-[13px] font-semibold text-[#0a0a0b] transition-colors hover:bg-[#e8c979] md:inline-flex">
            {t.navCta} <ArrowUpRight className="h-4 w-4" />
          </button>
          <button data-testid="mobile-menu-toggle" className="lg:hidden" onClick={() => setOpen(!open)} aria-label="menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="overflow-hidden border-t border-white/10 bg-[#0a0a0b]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="wrap flex flex-col gap-5 py-8">
              {t.nav.map((n) => (
                <button key={n.id} data-testid={`mobile-nav-${n.id}`} onClick={() => go(n.id)} className="text-left font-display text-3xl font-medium">{n.label}</button>
              ))}
              <button data-testid="mobile-cta-button" onClick={() => { setOpen(false); onContact(); }} className="btn-gold mt-4 w-fit">{t.navCta} <ArrowUpRight className="h-4 w-4" /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
